/**
 * The sidebar — design/PROTOTYPE-SPEC.md §2 for what is in it; the look is the
 * stone-and-green dashboard board (docs/DECISIONS.md, 10 October 2026).
 *
 * Wordmark and clinic, grouped nav with live counts, then the bottom stack:
 * trial notice, stock on hand, the "Logging as" switcher, plan and log out.
 *
 * ## Plan-gated items appear once they exist, and not before
 *
 * The spec shows Analytics, Promotions and Targets in the nav with PRO/PREMIUM
 * lock pills. A lock pill tells a clinic "pay more and this unlocks", so it may
 * only be shown when that is true — otherwise it advertises something nobody
 * can buy.
 *
 * **All three now exist**, so all three are in the nav with the pill their plan
 * requires — PRO for Analytics and Promotions, PREMIUM for Targets. The pill is
 * a courtesy; the API refuses the same requests with a 402 whatever the sidebar
 * renders (apps/api/src/lib/planGate.ts).
 *
 * The pill is a courtesy, not a boundary — the API refuses the same requests
 * with a 402 whatever the sidebar renders (apps/api/src/lib/planGate.ts).
 */
import type { ReactNode } from 'react';
import {
  PLAN_LABEL,
  ROLE_LABEL,
  TRIAL_DAYS,
  can,
  canAccess,
  requiredPlanFor,
  type Plan as CorePlan,
  type Role as CoreRole,
} from '@inventory-tracker/core';
import type { Me } from '../lib/api.js';
import { c, sans } from '../theme.js';
import { money } from './format.js';
import { useLogoUrl } from './logo.js';

export type Page =
  | 'dashboard'
  | 'inventory'
  | 'count'
  | 'expiry'
  | 'activity'
  | 'audit'
  | 'learn'
  | 'promotions'
  | 'analytics'
  | 'targets'
  | 'team'
  | 'manage'
  | 'account';

interface NavItem {
  page: Page;
  label: string;
  /** A count shown as a badge — omitted when zero. */
  badge?: number;
  /** Filled terracotta rather than outlined: the count that cannot wait. */
  urgent?: boolean;
  /** "PRO" / "PREMIUM" — shown when the current plan cannot reach the page. */
  lock?: string;
}

interface NavGroup {
  heading: string | null;
  items: NavItem[];
}

interface Props {
  me: Me;
  page: Page;
  onNavigate: (page: Page) => void;
  onSwitchUser: () => void;
  onSignOut: () => void;
  signingOut: boolean;
  /** From the dashboard load, so the sidebar reflects real stock. */
  stockValuePence: number | null;
  totalUnits: number | null;
  reorderCount: number | null;
  expiringCount: number | null;
  /** Set by the shell; on a phone it turns the sidebar into a slide-in menu. */
  className?: string;
}

export function Sidebar({
  me,
  page,
  onNavigate,
  onSwitchUser,
  onSignOut,
  signingOut,
  stockValuePence,
  totalUnits,
  reorderCount,
  expiringCount,
  className,
}: Props) {
  const logoUrl = useLogoUrl(me.account.logoVersion);
  const groups: NavGroup[] = [
    {
      heading: 'Stock',
      items: [
        { page: 'dashboard', label: 'Dashboard' },
        { page: 'inventory', label: 'Inventory', ...(reorderCount ? { badge: reorderCount } : {}) },
        { page: 'count', label: 'Stock count' },
        // The prototype badges Expiry, and only Expiry, with the expiring count
        // (design/Clinic Stock v2.dc.html:5283). Dropping it would leave the one
        // time-critical, irreversible number in the product unflagged in the nav
        // — stock that expires is money already lost. The reorder badge above is
        // an addition, not a replacement: spec §2 names "e.g. reorder count", so
        // both are wanted and they count different things.
        {
          page: 'expiry',
          label: 'Expiry',
          ...(expiringCount ? { badge: expiringCount, urgent: true } : {}),
        },
      ],
    },
    {
      heading: 'Grow',
      items: [
        {
          page: 'analytics',
          label: 'Analytics',
          ...(canAccess(me.subscription.plan as CorePlan, 'analytics')
            ? {}
            : { lock: PLAN_LABEL[requiredPlanFor('analytics')].toUpperCase() }),
        },
        {
          page: 'promotions',
          label: 'Promotions',
          ...(canAccess(me.subscription.plan as CorePlan, 'promotions')
            ? {}
            : { lock: PLAN_LABEL[requiredPlanFor('promotions')].toUpperCase() }),
        },
        {
          page: 'targets',
          label: 'Targets',
          ...(canAccess(me.subscription.plan as CorePlan, 'targets')
            ? {}
            : { lock: PLAN_LABEL[requiredPlanFor('targets')].toUpperCase() }),
        },
      ],
    },
    {
      heading: 'Records',
      items: [
        { page: 'activity', label: 'Activity' },
        // Role-gated, so it is HIDDEN rather than shown with a lock pill. A pill
        // means "pay more and this unlocks"; staff cannot buy their way past a
        // role, so a pill would be an offer the product cannot honour. The API
        // returns 403 regardless of what renders here.
        ...(can(me.user.role as CoreRole, 'viewAuditLog')
          ? [{ page: 'audit' as const, label: 'Audit log' }]
          : []),
      ],
    },
    {
      heading: 'Settings',
      items: [
        { page: 'team', label: 'Team' },
        { page: 'manage', label: 'Import & export' },
        { page: 'account', label: 'Account' },
      ],
    },
    { heading: 'Help', items: [{ page: 'learn', label: 'Learn' }] },
  ];

  const trialDaysLeft = me.subscription.trialEndsAt
    ? Math.max(
        0,
        Math.ceil((new Date(me.subscription.trialEndsAt).getTime() - Date.now()) / 86_400_000),
      )
    : null;

  const initials =
    me.user.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]!.toUpperCase())
      .join('') || '?';

  return (
    <aside
      className={className}
      style={{
        width: 248,
        flexShrink: 0,
        background: t.bg,
        borderRight: t.edge,
        color: t.text,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        position: 'sticky',
        top: 0,
        fontFamily: sans,
      }}
    >
      <div style={{ padding: '22px 18px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px' }}>
          <span
            aria-hidden="true"
            style={{
              width: 24,
              height: 24,
              borderRadius: 7,
              background: t.markBg,
              color: t.markText,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              fontWeight: 600,
              lineHeight: 1,
              paddingBottom: 2,
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          >
            g
          </span>
          <span
            style={{ fontSize: 16.5, fontWeight: 500, color: t.strong, letterSpacing: '-0.3px' }}
          >
            the good stock.
          </span>
        </div>

        {/* The clinic: its logo on a white tile when it has one, its name
            either way. Contained, not cropped: logos come in every shape. A
            logo that fails to load is not shown; see useLogoUrl. */}
        <div
          style={{
            marginTop: 16,
            background: t.panel,
            borderRadius: 14,
            padding: logoUrl ? 8 : '10px 12px',
          }}
        >
          {logoUrl && (
            <div
              style={{
                height: 64,
                borderRadius: 9,
                background: c.white,
                padding: 8,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={logoUrl}
                alt={`${me.account.clinicName} logo`}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </div>
          )}
          <div style={{ padding: logoUrl ? '8px 4px 2px' : 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: t.strong,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={me.account.clinicName}
            >
              {me.account.clinicName}
            </div>
            <div style={{ fontSize: 12.5, color: t.muted, marginTop: 1 }}>
              {PLAN_LABEL[me.subscription.plan]} plan
            </div>
          </div>
        </div>
      </div>

      {/*
        `minHeight: 0` is load-bearing, not tidying. A flex child defaults to
        `min-height: auto`, which means it refuses to shrink below its content —
        so `overflowY: auto` never engages, the nav grows to fit every item, and
        the blocks below it get pushed off the bottom of the viewport. Adding
        "Audit log" is what made it visible: on a short window "Activity" was
        clipped and the new item could not be clicked at all.
      */}
      <nav
        aria-label="Main"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '0 12px 12px',
          scrollbarWidth: 'thin',
          scrollbarColor: `${t.scroll} transparent`,
        }}
      >
        {groups.map((group, i) => (
          <div key={group.heading ?? 'top'}>
            {group.heading && (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: t.faint,
                  padding: i === 0 ? '4px 10px 6px' : '16px 10px 6px',
                }}
              >
                {group.heading}
              </div>
            )}
            {group.items.map((item) => (
              <NavButton
                key={item.page}
                page={item.page}
                active={page === item.page}
                onClick={() => onNavigate(item.page)}
                badge={item.badge}
                urgent={item.urgent}
                lock={item.lock}
              >
                {item.label}
              </NavButton>
            ))}
          </div>
        ))}
      </nav>

      <div style={{ flexShrink: 0, padding: '10px 12px 14px', display: 'grid', gap: 8 }}>
        {/* Stock on hand. Hidden until the dashboard has loaded rather than
            showing a £0 that would look like a real, alarming number. */}
        {stockValuePence !== null && (
          <div style={{ background: t.panel, borderRadius: 14, padding: '12px 14px' }}>
            <div style={{ fontSize: 12.5, color: t.muted }}>Stock on hand</div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 8,
                marginTop: 2,
              }}
            >
              <span
                style={{ fontSize: 22, fontWeight: 300, letterSpacing: '-0.6px', color: t.strong }}
              >
                {money(stockValuePence)}
              </span>
              {totalUnits !== null && (
                <span style={{ fontSize: 12.5, color: t.muted }}>{totalUnits} units</span>
              )}
            </div>
            {me.subscription.readOnly && (
              <div
                style={{
                  marginTop: 11,
                  paddingTop: 10,
                  borderTop: `1px solid ${t.line}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12.5,
                  color: t.muted,
                }}
              >
                <span>
                  {me.subscription.readOnlyReason === 'trial_ended' ? 'Free trial' : 'Subscription'}
                </span>
                <span style={{ color: c.amberText, fontWeight: 500 }}>Ended, read-only</span>
              </div>
            )}
            {trialDaysLeft !== null && trialDaysLeft > 0 && (
              <div style={{ marginTop: 11, paddingTop: 10, borderTop: `1px solid ${t.line}` }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12.5,
                    color: t.muted,
                  }}
                >
                  <span>Free trial</span>
                  <span style={{ color: t.text }}>
                    {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left
                  </span>
                </div>
                <div
                  style={{
                    height: 4,
                    borderRadius: 980,
                    background: t.line,
                    marginTop: 7,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, Math.max(4, ((TRIAL_DAYS - trialDaysLeft) / TRIAL_DAYS) * 100))}%`,
                      height: '100%',
                      borderRadius: 980,
                      background: t.accent,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* No stock card yet (first load): the trial still needs somewhere to show. */}
        {stockValuePence === null && trialDaysLeft !== null && trialDaysLeft > 0 && (
          <div style={{ fontSize: 12.5, color: t.muted, padding: '0 10px' }}>
            Free trial, {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            type="button"
            onClick={onSwitchUser}
            className="gs-nav"
            title="Switch to someone else on this device"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 'none',
              borderRadius: 14,
              padding: '7px 8px',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: sans,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: t.avatarBg,
                color: t.avatarText,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {initials}
            </span>
            <span style={{ minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  color: t.strong,
                  fontWeight: 500,
                  fontSize: 14,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {me.user.name}
              </span>
              <span style={{ display: 'block', color: t.muted, fontSize: 12.5 }}>
                {ROLE_LABEL[me.user.role as CoreRole]} · Switch
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            className="gs-nav"
            aria-label="Log out"
            title="Log out"
            style={{
              width: 36,
              height: 36,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              borderRadius: '50%',
              color: t.muted,
              cursor: signingOut ? 'progress' : 'pointer',
            }}
          >
            <Glyph d="M7 3H4.5A1.5 1.5 0 0 0 3 4.5v9A1.5 1.5 0 0 0 4.5 15H7M11.5 12.5 15 9l-3.5-3.5M15 9H7" />
          </button>
        </div>
      </div>
      <style>{`.gs-nav:hover{background:${t.hover} !important}`}</style>
    </aside>
  );
}

/**
 * The sidebar's colours. Emmanuel was shown both in the app on 13 October 2026
 * and chose light; the dark set is kept so switching back is one word.
 */
interface Tone {
  bg: string;
  edge: string;
  text: string;
  strong: string;
  muted: string;
  faint: string;
  panel: string;
  line: string;
  hover: string;
  scroll: string;
  markBg: string;
  markText: string;
  activeBg: string;
  activeText: string;
  activeShadow: string;
  icon: string;
  activeIcon: string;
  accent: string;
  avatarBg: string;
  avatarText: string;
  badgeBorder: string;
  activeBadgeBorder: string;
}

const TONES: Record<'dark' | 'light', Tone> = {
  dark: {
    bg: c.ink,
    edge: 'none',
    text: c.onDarkTick,
    strong: c.white,
    muted: c.onDarkFaint,
    faint: '#7C867D',
    panel: '#1C2620',
    line: '#2C3830',
    hover: '#1D2620',
    scroll: '#3A463D',
    markBg: '#F5F5F2',
    markText: c.ink,
    activeBg: c.green,
    activeText: c.white,
    activeShadow: 'none',
    icon: '#8F9A91',
    activeIcon: c.white,
    accent: '#7FA58F',
    avatarBg: c.green,
    avatarText: c.white,
    badgeBorder: '#3A463D',
    activeBadgeBorder: 'rgba(255,255,255,0.45)',
  },
  light: {
    bg: '#ECECE7',
    edge: '1px solid #E0E0DA',
    text: '#3A423C',
    strong: c.ink,
    muted: c.muted,
    faint: c.faint,
    panel: '#F7F7F4',
    line: '#E0E0DA',
    hover: '#E2E2DC',
    scroll: '#CFCFC8',
    markBg: c.ink,
    markText: '#F5F5F2',
    activeBg: c.white,
    activeText: c.ink,
    activeShadow: '0 1px 2px rgba(20,26,22,0.12)',
    icon: '#6C6F66',
    activeIcon: c.green,
    accent: c.green,
    avatarBg: c.green,
    avatarText: c.white,
    badgeBorder: '#CFCFC8',
    activeBadgeBorder: '#CFCFC8',
  },
};

const t = TONES.light;

/** One 18px line icon. Drawn here rather than pulled from an icon library. */
function Glyph({ d }: { d: string }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0, display: 'block' }}
    >
      <path d={d} />
    </svg>
  );
}

/** The drawing for each page's icon. */
const ICON: Record<Page, string> = {
  // Four tiles.
  dashboard: 'M3 3h5v5H3zM10 3h5v5h-5zM3 10h5v5H3zM10 10h5v5h-5z',
  // A box.
  inventory: 'M2.5 5.5 9 2.5l6.5 3v7L9 15.5l-6.5-3zM2.5 5.5 9 8.5l6.5-3M9 8.5v7',
  // A clipboard with a tick.
  count:
    'M6.5 3.5h5M5 3.5H4A1 1 0 0 0 3 4.5v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-10a1 1 0 0 0-1-1h-1M6.2 10l2 2 3.6-4',
  // A clock.
  expiry: 'M9 2.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM9 5.5V9l2.5 1.5',
  // Bars.
  analytics: 'M3 15.5h12M5 15.5V10M9 15.5V4M13 15.5V7.5',
  // A price tag.
  promotions:
    'M2.5 9.6V3.5a1 1 0 0 1 1-1h6.1a1 1 0 0 1 .7.3l5 5a1 1 0 0 1 0 1.4l-6.1 6.1a1 1 0 0 1-1.4 0l-5-5a1 1 0 0 1-.3-.7zM6 6h.01',
  // A target.
  targets:
    'M9 2.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM9 5.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7zM9 8.9h.01',
  // A pulse.
  activity: 'M2 9h3l2-5 4 10 2-5h3',
  // A page of lines.
  audit:
    'M4.5 2.5h9a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1zM6.5 6.5h5M6.5 9h5M6.5 11.5h3',
  // Two people.
  team: 'M6.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM2 15c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4M12 8a2.2 2.2 0 1 0 0-4.4M13 11.2c1.8.3 3 1.6 3 3.8',
  // In and out arrows.
  manage:
    'M5.5 14.500V3.500M5.5 3.5 2.5 6.500M5.5 3.500l3 3M12.5 3.500v11M12.5 14.500l-3-3M12.5 14.500l3-3',
  // Sliders.
  account: 'M3 5.500h6M13 5.500h2M3 12.500h2M9 12.500h6M11 3.500v4M7 10.500v4',
  // An open book.
  learn:
    'M9 4.500C7.5 3.5 5.5 3 3 3v11c2.5 0 4.5.500 6 1.5 1.5-1 3.5-1.5 6-1.500V3c-2.5 0-4.5.500-6 1.500zM9 4.500v11',
};

function NavButton({
  page,
  active,
  onClick,
  badge,
  urgent,
  lock,
  children,
}: {
  page: Page;
  active: boolean;
  onClick: () => void;
  badge?: number | undefined;
  urgent?: boolean | undefined;
  lock?: string | undefined;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={active ? undefined : 'gs-nav'}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        background: active ? t.activeBg : 'transparent',
        boxShadow: active ? t.activeShadow : 'none',
        border: 'none',
        borderRadius: 11,
        minHeight: 38,
        padding: '0 10px',
        marginBottom: 1,
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: sans,
        fontSize: 14.5,
        fontWeight: active ? 500 : 400,
        color: active ? t.activeText : t.text,
      }}
    >
      <span style={{ color: active ? t.activeIcon : t.icon, display: 'flex' }}>
        <Glyph d={ICON[page]} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {lock !== undefined && (
        // Not a disabled button: the page behind it explains what the plan
        // buys, which is the whole point of showing the item at all.
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: t.faint,
            border: `1px solid ${t.badgeBorder}`,
            borderRadius: 980,
            padding: '1px 7px',
          }}
        >
          {lock.charAt(0) + lock.slice(1).toLowerCase()}
        </span>
      )}
      {badge !== undefined && badge > 0 && (
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 980,
            padding: '1px 7px',
            minWidth: 9,
            textAlign: 'center',
            fontVariantNumeric: 'tabular-nums',
            ...(urgent
              ? { background: c.terracotta, color: c.white, border: `1px solid ${c.terracotta}` }
              : {
                  color: active ? t.activeText : t.text,
                  border: `1px solid ${active ? t.activeBadgeBorder : t.badgeBorder}`,
                }),
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}
